import { useEffect, useState } from "react";
import { requestPersistentStorage } from "@/lib/pwa";

// One place that knows about install: whether the native prompt is ready, the
// platform, and whether the app is already installed.
//
// The browser fires beforeinstallprompt once per page load, very early, often
// before React mounts. public/index.html stashes it on window before the app
// loads, and the module below catches it if the bundle loaded first.
//
// The offer stays visible for everyone who hasn't installed the app. "Not now"
// hides it for the current tab only, so it comes back on the next visit.

const GLOBAL_SLOT = "__hvDeferredInstallPrompt";
const GLOBAL_EVENT = "hv:installprompt";
const SESSION_DISMISS_KEY = "hv.install.dismissed-session";

let deferredPrompt = (typeof window !== "undefined" && window[GLOBAL_SLOT]) || null;
const subscribers = new Set();
const notifyAll = () => subscribers.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // suppress the mini-infobar
    deferredPrompt = e;
    window[GLOBAL_SLOT] = e;
    notifyAll();
  });
  window.addEventListener(GLOBAL_EVENT, () => {
    deferredPrompt = window[GLOBAL_SLOT] || deferredPrompt;
    notifyAll();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window[GLOBAL_SLOT] = null;
    requestPersistentStorage();
    notifyAll();
  });
}

export function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function detectPlatform() {
  const ua = window.navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  const isMac = /Macintosh/i.test(ua);
  const isSafari = /^((?!chrome|android|crios|edgios).)*safari/i.test(ua);
  if (isMac && isSafari) return "mac-safari";
  if (/Firefox/i.test(ua)) return "firefox";
  return "generic";
}

// "Install mobile app" or "Install desktop app", for the button label only.
export function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}

// "Not now" lasts for this tab only.
export function dismissForSession() {
  try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* ignore */ }
}

function readSessionDismissed() {
  try { return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"; } catch { return false; }
}

// Kept so existing sign-out code still works. Nothing persistent to clear now.
export function clearInstallDismissal() {
  try { sessionStorage.removeItem(SESSION_DISMISS_KEY); } catch { /* ignore */ }
}

export function useInstallPrompt() {
  const [prompt, setPrompt] = useState(() => deferredPrompt);
  const [platform] = useState(() => detectPlatform());
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => readSessionDismissed());
  const [notice, setNotice] = useState("");

  // Installed means this app is running as an installed app, or the browser
  // reports it is installed (Chromium on Android and desktop).
  useEffect(() => {
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    if (navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps().then((apps) => {
        if (apps && apps.length > 0) setInstalled(true);
      }).catch(() => { /* not supported here */ });
    }
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  useEffect(() => {
    const sync = () => setPrompt(deferredPrompt);
    subscribers.add(sync);
    sync();
    return () => subscribers.delete(sync);
  }, []);

  // Visible for every visitor who hasn't installed the app and hasn't said
  // "Not now" in this tab.
  const show = !installed && !dismissed;

  const install = async () => {
    if (!prompt || installing) return "unavailable";
    setInstalling(true);
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      // A deferred prompt is single-use, whatever the answer was.
      deferredPrompt = null;
      window[GLOBAL_SLOT] = null;
      notifyAll();
      setPrompt(null);
      if (outcome === "accepted") setInstalled(true);
      return outcome;
    } catch {
      deferredPrompt = null;
      notifyAll();
      setPrompt(null);
      return "error";
    } finally {
      setInstalling(false);
    }
  };

  const dismiss = () => {
    dismissForSession();
    setDismissed(true);
  };

  return { show, prompt, platform, installing, installed, install, dismiss, notice, setNotice };
}
